import { UserPermissions } from '@order-management/roles';
import { useCallback, useState } from 'react';
import { Element, Icon } from 'react-bulma-components';
import {
  FaDownload,
  FaFileLines,
  FaGear,
  FaKey,
  FaLaptop,
  FaUsers,
} from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';
import { downloadTemporaryAccessAuditLog } from '../../admin/admin-service';
import { AdminUserAccessActionsModal } from '../../admin/admin-user-access-actions-modal/admin-user-access-actions-modal';
import { SalesOrdersAccessStorageService } from '../../admin/sales-orders-access-storage-service';
import CheckPermissions from '../../shared/check-permissions/check-permissions';
import { useCSVDownload } from '../../shared/hooks/csv-download';
import useHttpRequest from '../../shared/hooks/http-request';
import SwitchToCustomerRoleModal from '../../zspace-internal/switch-to-customer-role-modal/switch-to-customer-role-modal';
import DropdownMenu from '../dropdown-menu/dropdown-menu';
import LoadingIcon from '../loading-icon/loading-icon';

const SELF_ASSIGNMENT_AUDIT_LOG_DOWNLOAD_ERROR_MESSAGE =
  'Self assignment Audit Log download failed. Please try again.';

const customerAccessSalesOrdersSessionStorageService =
  new SalesOrdersAccessStorageService();

export function AdminToolsDropdown() {
  const navigate = useNavigate();

  const {
    executeHttpRequest: executeSelfAssignmentAuditLogDownload,
    isLoading: isLoadingSelfAssignmentAuditLogDownload,
  } = useHttpRequest();

  const [showSwitchRoleModal, setShowSwitchRoleModal] =
    useState<boolean>(false);
  const [showUserAccessActionsModal, setShowUserAccessActionsModal] =
    useState<boolean>(false);

  const { downloadCSV } = useCSVDownload();

  const handleUserAccessClick = useCallback(() => {
    setShowUserAccessActionsModal(true);
  }, []);

  const handleDownloadSelfAssignmentAuditLog = useCallback(
    () =>
      executeSelfAssignmentAuditLogDownload({
        asyncFunction: async () => {
          const selfAssignmentAuditLogDownloadableData =
            await downloadTemporaryAccessAuditLog();
          downloadCSV(selfAssignmentAuditLogDownloadableData);
        },
        customErrorMessage: SELF_ASSIGNMENT_AUDIT_LOG_DOWNLOAD_ERROR_MESSAGE,
      }),
    [downloadCSV, executeSelfAssignmentAuditLogDownload]
  );

  const navigateToSelectTemporaryPermissionsPage = useCallback(() => {
    setShowSwitchRoleModal(false);
    setShowUserAccessActionsModal(false);
    navigate('/customer-management');
  }, [navigate]);

  const navigateToDevices = useCallback(() => {
    navigate('/admin/devices');
  }, [navigate]);

  const navigateToSalesOrders = useCallback(() => {
    navigate('/admin/sales-orders');
  }, [navigate]);

  const navigateToLicenseKeys = useCallback(() => {
    navigate('/admin/license-keys');
  }, [navigate]);

  const navigateToManageTemporaryAccess = useCallback(() => {
    setShowUserAccessActionsModal(false);
    navigate('/admin/temporary-access');
  }, [navigate]);

  const navigateToCustomerAccessSetup = useCallback(() => {
    setShowUserAccessActionsModal(false);
    customerAccessSalesOrdersSessionStorageService.remove();
    navigate('/admin/customer-access/setup/sales-orders');
  }, [navigate]);

  const navigateToUserControlPanel = useCallback(() => {
    setShowUserAccessActionsModal(false);
    navigate('/users');
  }, [navigate]);

  return (
    <>
      <DropdownMenu
        label={
          <Element display="flex" alignItems="center" className="gap-1">
            <Icon color="primary-dark">
              <FaGear />
            </Icon>
            <span className="has-text-primary-dark is-underlined">
              Admin tools
            </span>
          </Element>
        }
      >
        <DropdownMenu.Item onClick={navigateToDevices}>
          <Element display="flex" alignItems="center" className="gap-1">
            <Icon color="primary-dark">
              <FaLaptop />
            </Icon>
            <span className="is-size-6">Devices</span>
          </Element>
        </DropdownMenu.Item>

        <CheckPermissions
          permissions={
            UserPermissions.USERS_SELF_ASSIGNMENTS_DOWNLOAD_AUDIT_LOGS
          }
        >
          <CheckPermissions.Render>
            <DropdownMenu.Item onClick={handleDownloadSelfAssignmentAuditLog}>
              <Element display="flex" alignItems="center" className="gap-1">
                <LoadingIcon
                  isLoading={isLoadingSelfAssignmentAuditLogDownload}
                  icon={FaDownload}
                  iconColor="primary-dark"
                />
                <span className="is-size-6">Self assignment Audit Log</span>
              </Element>
            </DropdownMenu.Item>
          </CheckPermissions.Render>
        </CheckPermissions>

        <DropdownMenu.Item onClick={navigateToLicenseKeys}>
          <Element display="flex" alignItems="center" className="gap-1">
            <Icon color="primary-dark">
              <FaKey />
            </Icon>
            <span className="is-size-6">License Keys</span>
          </Element>
        </DropdownMenu.Item>

        <DropdownMenu.Item onClick={navigateToSalesOrders}>
          <Element display="flex" alignItems="center" className="gap-1">
            <Icon color="primary-dark">
              <FaFileLines />
            </Icon>
            <span className="is-size-6">Sales Orders</span>
          </Element>
        </DropdownMenu.Item>

        <DropdownMenu.Item onClick={handleUserAccessClick}>
          <Element display="flex" alignItems="center" className="gap-1">
            <Icon color="primary-dark">
              <FaUsers />
            </Icon>
            <span className="is-size-6">User Access</span>
          </Element>
        </DropdownMenu.Item>
      </DropdownMenu>

      <SwitchToCustomerRoleModal
        show={showSwitchRoleModal}
        onClose={() => setShowSwitchRoleModal(false)}
        onSwitchRole={navigateToSelectTemporaryPermissionsPage}
      />
      <AdminUserAccessActionsModal
        show={showUserAccessActionsModal}
        onClose={() => setShowUserAccessActionsModal(false)}
        onManageTemporaryAccess={navigateToManageTemporaryAccess}
        onManageCustomerSalesOrders={navigateToSelectTemporaryPermissionsPage}
        onAddSalesOrderManagers={navigateToCustomerAccessSetup}
        onManageInternalUsers={navigateToUserControlPanel}
      />
    </>
  );
}

export default AdminToolsDropdown;
